import React from 'react';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

/**
 * Check-in — rota `/check-in`, dentro do layout com sidebar (item "Check-in" no grupo "Operação").
 * Prints: 10-checkin-operacoes, 11-checkin-console, 12-checkin-confirmar, 13-checkin-sucesso
 * (skill `aliste-se-admin-kit`).
 *
 * Fluxo: escolher operação → console (busca + lista) → confirmar (dialog) → sucesso (plaqueta com o número).
 * Teclado: Enter na busca abre a confirmação do 1º "aguardando" da lista; Enter confirma; Enter/Esc fecha o sucesso.
 * Animações: utilities `anim-ci-in`, `anim-ci-stamp`, `anim-ci-pulse` (ver styles/globals.css).
 */

export type CheckInSoldier = {
  id: string;
  name: string;
  age?: number | null;
  city?: string;
  photo?: string | null;
  paid: boolean;
  soldierNumber?: number | null;
  /** "HH:mm" do check-in (checkedInAt formatado). */
  checkedAt?: string | null;
};

export type CheckInEvent = {
  id: string;
  kind: string;
  name: string;
  dateLabel: string;
  city: string;
  live: boolean;
  total: number;
  done: number;
};

export type CheckInFilter = 'waiting' | 'unpaid' | 'done' | 'all';

export type CheckInResult = { name: string; number: number; paidAtGate: boolean };

export const padNumber = (n?: number | null) => (n ? String(n).padStart(3, '0') : '');
const brl = (n: number) =>
  `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const initials = (n: string) =>
  n
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
const subtitle = (s: CheckInSoldier) =>
  [s.age != null && `${s.age} anos`, s.city].filter(Boolean).join(' • ');

/* ─────────────── Topo ─────────────── */

/**
 * Pílula "Portão aberto" da direita do topo (vai no `action` do `AdminTopbar`). No celular fica só
 * o ponto pulsando, pra não espremer o título.
 */
export const GateOpenPill: React.FC = () => (
  <span
    title="Portão aberto"
    className="col-label flex size-8 shrink-0 items-center justify-center gap-2 rounded-full border border-olive-line/40 bg-accent text-olive-ink sm:w-auto sm:px-3"
  >
    <span className="anim-ci-pulse size-[7px] rounded-full bg-olive-icon" />
    <span className="sr-only sm:not-sr-only">Portão aberto</span>
  </span>
);

/* ─────────────── 1. Escolha da operação ─────────────── */

export const CheckInIntro: React.FC = () => (
  <div className="flex flex-col gap-2">
    <span className="col-label flex items-center gap-2 tracking-[.18em] text-sand-dim">
      <span className="h-px w-[18px] bg-sand-dim" />
      Controle de entrada
    </span>
    <span className="font-display text-[clamp(26px,2.6vw,36px)] font-extrabold leading-[1.05] tracking-[-.03em] text-white">
      Qual operação vai receber o efetivo?
    </span>
    <span className="max-w-[620px] text-sm text-pretty text-muted-foreground">
      Ao identificar um recruta, ele recebe o próximo número da operação e o pagamento
      pendente é confirmado automaticamente.
    </span>
  </div>
);

export const CheckInEventCard: React.FC<{
  event: CheckInEvent;
  coverUrl: string;
  onOpen: () => void;
}> = ({ event: e, coverUrl, onOpen }) => {
  const pct = e.total ? Math.round((e.done / e.total) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'relative flex min-h-[320px] overflow-hidden rounded-[18px] border bg-cover bg-[center_40%] text-left transition-[border-color,transform] duration-200 ease-[cubic-bezier(.2,.8,.2,1)] hover:-translate-y-0.5 hover:border-ring',
        e.live ? 'border-olive-line' : 'border-border',
      )}
      style={{ backgroundImage: `url(${coverUrl})` }}
    >
      <span
        className={cn(
          'absolute inset-0',
          e.live
            ? 'bg-[linear-gradient(180deg,color-mix(in_oklch,var(--sunken)_55%,transparent)_0%,color-mix(in_oklch,var(--sunken)_92%,transparent)_70%)]'
            : 'bg-[linear-gradient(180deg,color-mix(in_oklch,var(--sunken)_82%,transparent)_0%,color-mix(in_oklch,var(--sunken)_97%,transparent)_70%)]',
        )}
      />
      <div className="relative flex flex-1 flex-col gap-[18px] p-6">
        <span className="flex items-center gap-2.5">
          <span className="font-display rounded-[7px] border border-border bg-sunken/70 px-[9px] py-1 text-[11px] font-extrabold uppercase tracking-[.06em] text-foreground">
            {e.kind}
          </span>
          <span
            className={cn(
              'col-label ml-auto flex h-6 items-center gap-[7px] rounded-full border bg-sunken/70 px-2.5',
              e.live
                ? 'border-olive-line/40 text-olive-ink'
                : 'border-border text-muted-foreground',
            )}
          >
            <span className="size-1.5 rounded-full bg-current" />
            {e.live ? 'Hoje' : 'Em breve'}
          </span>
        </span>
        <span className="flex flex-col gap-2.5">
          <span className="font-display text-[30px] font-extrabold uppercase leading-[.98] tracking-[-.03em] text-white">
            {e.name}
          </span>
          <span className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-muted-foreground">
            <span className="flex items-center gap-[7px]">
              <Icon name="calendar" className="size-3.5 text-olive-icon" />
              {e.dateLabel}
            </span>
            <span className="flex items-center gap-[7px]">
              <Icon name="map-pin" className="size-3.5 text-olive-icon" />
              {e.city}
            </span>
          </span>
        </span>
        <div className="mt-auto flex flex-col gap-3">
          <span className="flex items-end gap-3">
            <span className="font-display text-[52px] font-extrabold leading-[.8] tracking-[-.05em] text-sand">
              {e.done}
            </span>
            <span className="col-label flex flex-col gap-[3px] pb-0.5 text-muted-foreground">
              <span>de {e.total}</span>
              <span>identificados</span>
            </span>
            <span
              className={cn(
                'ml-auto flex h-[42px] items-center gap-2 rounded-full border px-[18px] text-[13px] font-bold',
                e.live
                  ? 'border-sand bg-sand text-on-sand'
                  : 'border-border bg-sunken/60 text-foreground',
              )}
            >
              {e.live ? 'Iniciar check-in' : 'Abrir'}
              <Icon name="chevron-right" className="size-[15px]" />
            </span>
          </span>
          <span className="flex h-1.5 overflow-hidden rounded-full bg-sand/10">
            <span className="rounded-full bg-olive-icon" style={{ width: `${pct}%` }} />
          </span>
        </div>
      </div>
    </button>
  );
};

export const CheckInNoEvents: React.FC = () => (
  <div className="flex flex-col items-center gap-2.5 rounded-[18px] border border-dashed border-ring px-6 py-14 text-center">
    <Icon name="calendar-off" className="size-6 text-muted-foreground" />
    <span className="font-display text-base font-extrabold uppercase tracking-[-.01em] text-white">
      Nenhuma operação próxima
    </span>
    <span className="text-[13px] text-muted-foreground">
      O check-in abre para operações ativas em andamento ou com data futura.
    </span>
  </div>
);

/* ─────────────── 2. Console ─────────────── */

export const CheckInStats: React.FC<{
  done: number;
  total: number;
  waiting: number;
  unpaid: number;
  nextNumber: number;
  sequence: string;
}> = ({ done, total, waiting, unpaid, nextNumber, sequence }) => {
  const pct = total ? `${Math.round((done / total) * 100)}%` : '0%';
  return (
    <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-border bg-card sm:grid-cols-4">
      <div className="col-span-2 flex min-w-0 flex-col gap-3 border-b border-border px-[22px] py-5 sm:border-b-0">
        <span className="flex items-baseline gap-2.5">
          <span className="col-label text-muted-foreground">Efetivo identificado</span>
          <span className="font-display ml-auto text-sm font-extrabold text-olive-ink">
            {pct}
          </span>
        </span>
        <span className="flex items-baseline gap-2">
          <span className="font-display text-[40px] font-extrabold leading-none tracking-[-.04em] text-white">
            {done}
          </span>
          <span className="font-display text-lg font-extrabold text-faint">
            / {total}
          </span>
        </span>
        <span className="flex h-1.5 overflow-hidden rounded-full bg-sand/[.08]">
          <span
            className="rounded-full bg-olive-icon transition-[width] duration-400 ease-[cubic-bezier(.2,.8,.2,1)]"
            style={{ width: pct }}
          />
        </span>
      </div>
      <div className="flex min-w-0 flex-col gap-2.5 border-border px-[22px] py-5 sm:border-l">
        <span className="col-label text-muted-foreground">Aguardando</span>
        <span className="font-display text-[32px] font-extrabold leading-none tracking-[-.035em] text-white">
          {waiting}
        </span>
        <span className={cn('text-xs', unpaid ? 'text-sand' : 'text-muted-foreground')}>
          {unpaid} com pagamento pendente
        </span>
      </div>
      <div className="flex min-w-0 flex-col gap-2.5 border-l border-border bg-[linear-gradient(180deg,color-mix(in_oklch,var(--sand)_5%,transparent),transparent)] px-[22px] py-5">
        <span className="col-label text-sand-dim">Próximo nº</span>
        <span className="font-display text-[32px] font-extrabold leading-none tracking-[-.035em] text-sand">
          {padNumber(nextNumber)}
        </span>
        <span className="text-xs text-muted-foreground">Sequência {sequence}</span>
      </div>
    </div>
  );
};

export const CheckInSearch = React.forwardRef<
  HTMLInputElement,
  { value: string; onChange: (v: string) => void; onEnter: () => void; canEnter: boolean }
>(({ value, onChange, onEnter, canEnter }, ref) => (
  <label className="group relative flex h-[68px] cursor-text items-center gap-3.5 rounded-2xl border border-border bg-field px-5 transition-colors focus-within:border-olive-line focus-within:bg-accent">
    <span className="absolute inset-y-4 left-0 w-[3px] rounded-full bg-olive-icon opacity-0 transition-opacity group-focus-within:opacity-100" />
    <Icon name="search" className="size-[22px] shrink-0 text-olive-icon" />
    <input
      ref={ref}
      // biome-ignore lint/a11y/noAutofocus: o operador faz a fila inteira pelo teclado (skill aliste-se-admin-kit).
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onEnter();
        }
      }}
      placeholder="Nome do recruta ou nº…"
      className="min-w-0 flex-1 border-0 bg-transparent text-[19px] font-semibold tracking-[-.01em] text-white outline-none placeholder:text-faint"
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="col-label text-faint hover:text-sand"
      >
        Limpar
      </button>
    )}
    <span className="col-label hidden shrink-0 items-center gap-1.5 tracking-[.12em] text-faint sm:flex">
      <span className="rounded-md border border-border px-[7px] py-[3px] text-muted-foreground">
        Enter
      </span>
      {canEnter && value ? 'identifica 1º' : 'confirma'}
    </span>
  </label>
));
CheckInSearch.displayName = 'CheckInSearch';

/** Filtro em pílula — selecionado inverte para branco com texto preto. */
export const CheckInFilterChip: React.FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex h-9 items-center gap-2 rounded-full border px-3.5 text-[12.5px] font-bold transition-colors duration-100',
      active
        ? 'border-white bg-white text-background'
        : 'border-border bg-transparent text-muted-foreground hover:border-ring',
    )}
  >
    {label}
    <span
      className={cn(
        'font-display flex h-5 min-w-[22px] items-center justify-center rounded-full px-1.5 text-[11px] font-extrabold',
        active ? 'bg-black/10' : 'bg-sand/[.08]',
      )}
    >
      {count}
    </span>
  </button>
);

export const SoldierAvatar: React.FC<{
  name: string;
  photo?: string | null;
  size?: 'md' | 'lg';
  ring?: boolean;
}> = ({ name, photo, size = 'md', ring }) => {
  // Foto que não carrega (arquivo antigo que não veio na migração) cai para as iniciais.
  const [broken, setBroken] = React.useState(false);
  const cls = cn(
    'shrink-0 rounded-full',
    size === 'lg' ? 'size-[72px]' : 'size-[42px]',
    ring ? 'border-2 border-sand' : 'border border-border',
  );
  return photo && !broken ? (
    <img
      src={photo}
      alt=""
      onError={() => setBroken(true)}
      className={cn(cls, 'object-cover')}
    />
  ) : (
    <span
      className={cn(
        cls,
        'font-display flex items-center justify-center bg-sunken font-extrabold',
        size === 'lg' ? 'text-[22px] text-sand' : 'text-[13px] text-muted-foreground',
      )}
    >
      {initials(name)}
    </span>
  );
};

export const CheckInRow: React.FC<{
  soldier: CheckInSoldier;
  canCheckIn: boolean;
  onCheckIn: () => void;
}> = ({ soldier: s, canCheckIn, onCheckIn }) => {
  const done = !!s.soldierNumber;
  return (
    <div className="flex items-center gap-3.5 border-b border-border py-3 pl-4 pr-[18px] transition-colors last:border-b-0 hover:bg-raised">
      <span
        className={cn(
          'h-[34px] w-[3px] shrink-0 rounded-full',
          done ? 'bg-olive-icon' : !s.paid ? 'bg-sand' : 'bg-border',
        )}
      />
      <SoldierAvatar name={s.name} photo={s.photo} />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          className={cn(
            'truncate text-[15px] font-semibold',
            done ? 'text-muted-foreground' : 'text-white',
          )}
        >
          {s.name}
        </span>
        <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-faint">
          <span>{subtitle(s)}</span>
          {!s.paid && (
            <span className="flex items-center gap-1.5 font-semibold text-sand">
              <span className="size-[5px] rounded-full bg-sand" />
              Pagamento pendente
            </span>
          )}
        </span>
      </span>
      {done ? (
        <span className="flex shrink-0 items-center gap-2.5">
          {s.checkedAt && <span className="text-[11.5px] text-faint">{s.checkedAt}</span>}
          <span className="flex h-9 items-center gap-[7px] rounded-[10px] border border-olive-line/40 bg-accent pl-2.5 pr-3 text-olive-ink">
            <Icon name="check" className="size-3.5" strokeWidth={2.4} />
            <span className="font-display text-[15px] font-extrabold tracking-[-.01em]">
              Nº {padNumber(s.soldierNumber)}
            </span>
          </span>
        </span>
      ) : (
        canCheckIn && (
          <button
            type="button"
            onClick={onCheckIn}
            className="flex h-10 shrink-0 items-center gap-2 rounded-full bg-sand px-4 text-[13px] font-bold text-on-sand sm:px-[18px] transition-[background-color,transform] duration-100 hover:bg-sand/90 active:scale-[.97]"
          >
            <Icon name="circle-check" className="hidden size-4 sm:block" />
            Check-in
          </button>
        )
      )}
    </div>
  );
};

export const CheckInEmpty: React.FC = () => (
  <div className="flex flex-col items-center gap-2.5 px-6 py-12 text-center">
    <span className="flex size-12 items-center justify-center rounded-full border border-dashed border-ring text-muted-foreground">
      <Icon name="search" className="size-5" />
    </span>
    <span className="font-display text-base font-extrabold uppercase tracking-[-.01em] text-white">
      Nenhum recruta encontrado
    </span>
    <span className="text-[13px] text-muted-foreground">
      Confira a grafia ou procure pelo número da inscrição.
    </span>
  </div>
);

export const CheckInFeed: React.FC<{
  items: CheckInSoldier[];
  since?: string | null;
}> = ({ items, since }) => (
  <div className="overflow-hidden rounded-2xl border border-border bg-card">
    <div className="flex items-center gap-2.5 border-b border-border px-5 py-[18px]">
      <span className="font-display mr-auto text-[15px] font-extrabold uppercase tracking-[-.02em] text-white">
        Últimas entradas
      </span>
      {since && (
        <span className="text-[11.5px] text-muted-foreground">
          {items.length} desde {since}
        </span>
      )}
    </div>
    {items.length === 0 && (
      <span className="block px-5 py-6 text-center text-[12.5px] text-faint">
        Nenhum recruta identificado ainda.
      </span>
    )}
    {items.map((s, i) => (
      <div
        key={s.id}
        className="flex items-center gap-3 border-b border-border px-5 py-3 last:border-b-0"
      >
        <span
          className={cn(
            'font-display flex h-[30px] min-w-12 items-center justify-center rounded-lg border px-2 text-[13px] font-extrabold',
            i === 0
              ? 'border-sand bg-sand text-on-sand'
              : 'border-olive-line/40 bg-accent text-olive-ink',
          )}
        >
          {padNumber(s.soldierNumber)}
        </span>
        <span className="flex-1 truncate text-[13px] font-semibold text-foreground">
          {s.name}
        </span>
        <span className="text-[11.5px] text-faint">{s.checkedAt}</span>
      </div>
    ))}
  </div>
);

export const GateCashCard: React.FC<{ amount: number }> = ({ amount }) => (
  <div className="flex flex-col gap-3.5 rounded-2xl border border-border bg-card px-5 py-[18px]">
    <span className="flex items-center gap-2.5">
      <Icon name="wallet" className="size-4 text-olive-icon" />
      <span className="col-label text-muted-foreground">Recebido no portão</span>
    </span>
    <span className="font-display text-[30px] font-extrabold leading-none tracking-[-.035em] text-sand">
      {brl(amount)}
    </span>
    <span className="text-[12.5px] leading-normal text-pretty text-muted-foreground">
      {amount
        ? 'Pagamentos pendentes confirmados durante o check-in desta operação.'
        : 'Nenhum pagamento recebido no portão ainda. Pendências são confirmadas no check-in.'}
    </span>
  </div>
);

/* ─────────────── 3. Confirmação ─────────────── */

export const CheckInConfirmDialog: React.FC<{
  soldier: CheckInSoldier | null;
  nextNumber: number;
  price: number;
  coverUrl: string;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ soldier: s, nextNumber, price, coverUrl, loading, onCancel, onConfirm }) => {
  React.useEffect(() => {
    if (!s) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && !loading) {
        e.preventDefault();
        onConfirm();
      }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [s, loading, onCancel, onConfirm]);
  if (!s) return null;
  return (
    <div
      onClick={onCancel}
      className="fixed inset-0 z-40 flex items-center justify-center bg-[rgba(3,4,2,.78)] p-5 backdrop-blur-[6px]"
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="anim-ci-in w-full max-w-[520px] overflow-hidden rounded-3xl border border-border bg-card shadow-[0_30px_80px_rgba(0,0,0,.7)]"
      >
        <div
          className="relative flex items-center gap-[18px] bg-cover bg-[center_40%] px-[26px] pb-[22px] pt-[26px]"
          style={{
            backgroundImage: `linear-gradient(180deg,color-mix(in oklch,var(--card) 60%,transparent),var(--card)),url(${coverUrl})`,
          }}
        >
          <SoldierAvatar name={s.name} photo={s.photo} size="lg" ring />
          <span className="flex min-w-0 flex-col gap-1.5">
            <span className="col-label tracking-[.16em] text-sand-dim">
              Confirmar identificação
            </span>
            <span className="font-display text-2xl font-extrabold uppercase leading-[1.02] tracking-[-.025em] text-white">
              {s.name}
            </span>
            <span className="text-[12.5px] text-muted-foreground">{subtitle(s)}</span>
          </span>
        </div>
        <div className="flex flex-col gap-4 px-[26px] pb-[26px] pt-[22px]">
          <div className="flex items-center gap-4 rounded-[14px] border border-border bg-sunken px-[18px] py-4">
            <span className="mr-auto flex flex-col gap-[5px]">
              <span className="col-label text-muted-foreground">
                Número a ser atribuído
              </span>
              <span className="text-[12.5px] text-faint">
                Gravar na plaqueta do recruta
              </span>
            </span>
            <span className="font-display text-[44px] font-extrabold leading-[.9] tracking-[-.04em] text-sand">
              {padNumber(nextNumber)}
            </span>
          </div>
          {!s.paid && (
            <span className="flex items-start gap-3 rounded-xl border border-dashed border-sand/35 px-4 py-3.5 text-[13px] leading-normal text-muted-foreground">
              <Icon
                name="triangle-alert"
                className="mt-px size-[17px] shrink-0 text-sand"
              />
              <span>
                Pagamento pendente. Ao confirmar,{' '}
                <strong className="font-bold text-sand">{brl(price)}</strong> será
                registrado como recebido no portão.
              </span>
            </span>
          )}
          <span className="text-xs text-faint">Esta ação não pode ser desfeita.</span>
          <div className="flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={onCancel}
              className="flex h-[50px] flex-[1_1_140px] items-center justify-center rounded-full border border-border text-sm font-semibold text-muted-foreground hover:border-ring hover:text-white"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="flex h-[50px] flex-[2_1_220px] items-center justify-center gap-[9px] rounded-full bg-sand text-sm font-bold text-on-sand transition-[background-color,transform] hover:bg-sand/90 active:scale-[.97] disabled:opacity-70"
            >
              {loading ? (
                <span className="size-4 animate-spin rounded-full border-2 border-on-sand/25 border-t-on-sand" />
              ) : (
                <Icon name="circle-check" className="size-[17px]" />
              )}
              {s.paid ? 'Confirmar check-in' : 'Receber e identificar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── 4. Sucesso ─────────────── */

export const CheckInSuccess: React.FC<{
  result: CheckInResult | null;
  operationTag: string;
  yearLabel: string;
  price: number;
  onNext: () => void;
}> = ({ result, operationTag, yearLabel, price, onNext }) => {
  React.useEffect(() => {
    if (!result) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [result, onNext]);
  if (!result) return null;
  return (
    <div
      onClick={onNext}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(3,4,2,.9)] p-5 backdrop-blur-[10px]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="anim-ci-in flex w-full max-w-[460px] flex-col items-center gap-[22px] text-center"
      >
        <span className="col-label flex h-[30px] items-center gap-2 rounded-full border border-olive-line/40 bg-accent px-3.5 text-[10.5px] tracking-[.16em] text-olive-ink">
          <Icon name="check" className="size-[13px]" strokeWidth={2.6} />
          Recruta identificado
        </span>
        {/* Plaqueta (dog tag): moldura metálica areia 3px, raio 40, furo à esquerda. */}
        <div className="anim-ci-stamp relative h-[190px] w-[300px] rounded-[40px] bg-[linear-gradient(145deg,oklch(0.94_0.06_94),oklch(0.6_0.035_100)_45%,var(--sand)_70%,oklch(0.5_0.025_105))] p-[3px] shadow-[0_30px_80px_rgba(0,0,0,.8)]">
          <div className="relative flex size-full flex-col items-center justify-center gap-1 overflow-hidden rounded-[37px] bg-[linear-gradient(160deg,var(--raised),var(--field))]">
            <span className="absolute left-[22px] top-1/2 -mt-[9px] size-[18px] rounded-full border-2 border-sand/30 bg-black" />
            <span className="col-label tracking-[.24em] text-sand-dim">
              {operationTag}
            </span>
            <span className="font-display text-[96px] font-extrabold leading-[.95] tracking-[-.05em] text-sand">
              {padNumber(result.number)}
            </span>
            <span className="col-label tracking-[.24em] text-faint">{yearLabel}</span>
          </div>
        </div>
        <span className="flex flex-col gap-2">
          <span className="font-display text-[26px] font-extrabold uppercase leading-[1.05] tracking-[-.025em] text-white">
            {result.name}
          </span>
          <span className="text-[13.5px] text-muted-foreground">
            {result.paidAtGate
              ? `Pagamento de ${brl(price)} registrado. Entregue a plaqueta ao recruta.`
              : 'Entregue a plaqueta e encaminhe ao alojamento.'}
          </span>
        </span>
        <button
          type="button"
          // biome-ignore lint/a11y/noAutofocus: Enter continua a fila sem mouse.
          autoFocus
          onClick={onNext}
          className="flex h-[52px] items-center gap-[9px] rounded-full bg-sand px-[30px] text-sm font-bold text-on-sand hover:bg-sand/90"
        >
          Próximo recruta
          <Icon name="chevron-right" className="size-4" />
        </button>
        <span className="text-[11px] text-faint">Pressione Enter para continuar</span>
      </div>
    </div>
  );
};

/* ─────────────── Console completo ─────────────── */

/**
 * Tela do console. A página busca `GET /check-in/events/:id` e passa `soldiers`/`nextNumber`;
 * `onCheckIn` chama `POST /check-in/soldiers/:id` e devolve o número oficial. Se falhar, a página
 * mostra o erro e recarrega a lista — aqui só fecha a confirmação.
 */
export const CheckInConsole: React.FC<{
  sequence: string;
  soldiers: CheckInSoldier[];
  nextNumber: number;
  price: number;
  coverUrl: string;
  since?: string | null;
  gateCash: number;
  canCheckIn: boolean;
  onCheckIn: (s: CheckInSoldier) => Promise<CheckInResult | null>;
}> = ({
  sequence,
  soldiers,
  nextNumber,
  price,
  coverUrl,
  since,
  gateCash,
  canCheckIn,
  onCheckIn,
}) => {
  const [query, setQuery] = React.useState('');
  const [filter, setFilter] = React.useState<CheckInFilter>('waiting');
  const [selId, setSelId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState<CheckInResult | null>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const done = soldiers.filter((s) => s.soldierNumber);
  const waiting = soldiers.filter((s) => !s.soldierNumber);
  const unpaid = waiting.filter((s) => !s.paid);

  const q = norm(query.trim());
  const match: Record<CheckInFilter, (s: CheckInSoldier) => boolean> = {
    waiting: (s) => !s.soldierNumber,
    unpaid: (s) => !s.soldierNumber && !s.paid,
    done: (s) => !!s.soldierNumber,
    all: () => true,
  };
  const list = soldiers
    .filter((s) =>
      q
        ? norm(s.name).includes(q) || padNumber(s.soldierNumber).includes(q)
        : match[filter](s),
    )
    .sort(
      (a, b) =>
        Number(!!a.soldierNumber) - Number(!!b.soldierNumber) ||
        a.name.localeCompare(b.name),
    );
  const firstWaiting = list.find((s) => !s.soldierNumber);
  const sel = soldiers.find((s) => s.id === selId) ?? null;
  const feed = [...done]
    .sort((a, b) => (b.soldierNumber || 0) - (a.soldierNumber || 0))
    .slice(0, 6);

  const focus = () => setTimeout(() => searchRef.current?.focus(), 30);
  // A busca perde o foco com a confirmação aberta: o Enter é do dialog, não dela.
  const select = (id: string) => {
    if (!canCheckIn) return;
    searchRef.current?.blur();
    setSelId(id);
  };
  const cancel = React.useCallback(() => {
    setSelId(null);
    focus();
  }, []);
  const confirm = React.useCallback(async () => {
    if (!sel) return;
    setLoading(true);
    try {
      const res = await onCheckIn(sel);
      setSelId(null);
      if (res) setResult(res);
      else focus();
    } finally {
      setLoading(false);
    }
  }, [sel, onCheckIn]);
  const next = React.useCallback(() => {
    setResult(null);
    setQuery('');
    focus();
  }, []);

  const filters: { id: CheckInFilter; label: string; count: number }[] = [
    { id: 'waiting', label: 'Aguardando', count: waiting.length },
    { id: 'unpaid', label: 'Pag. pendente', count: unpaid.length },
    { id: 'done', label: 'Identificados', count: done.length },
    { id: 'all', label: 'Todos', count: soldiers.length },
  ];

  return (
    <>
      <div className="flex w-full max-w-[1480px] flex-wrap items-start gap-5">
        <div className="flex min-w-0 flex-[1_1_620px] flex-col gap-4">
          <CheckInStats
            done={done.length}
            total={soldiers.length}
            waiting={waiting.length}
            unpaid={unpaid.length}
            nextNumber={nextNumber}
            sequence={sequence}
          />
          <CheckInSearch
            ref={searchRef}
            value={query}
            onChange={setQuery}
            canEnter={!!firstWaiting && canCheckIn}
            onEnter={() => firstWaiting && select(firstWaiting.id)}
          />
          <div className="flex flex-wrap items-center gap-2">
            {filters.map((f) => (
              <CheckInFilterChip
                key={f.id}
                label={f.label}
                count={f.count}
                active={!q && filter === f.id}
                onClick={() => {
                  setFilter(f.id);
                  setQuery('');
                }}
              />
            ))}
            <span className="col-label ml-auto text-[10.5px] text-faint">
              {q
                ? `${list.length} resultado${list.length === 1 ? '' : 's'} para a busca`
                : `Exibindo ${list.length}`}
            </span>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            {list.map((s) => (
              <CheckInRow
                key={s.id}
                soldier={s}
                canCheckIn={canCheckIn}
                onCheckIn={() => select(s.id)}
              />
            ))}
            {list.length === 0 && <CheckInEmpty />}
          </div>
        </div>
        {/* Sticky só lado a lado; `top-0` = topo da área rolável do layout (o `top-24` do kit contava a partir dela e descia a coluna). */}
        <aside className="flex min-w-0 max-w-full flex-[1_1_300px] flex-col gap-4 lg:sticky lg:top-0">
          <CheckInFeed items={feed} since={since} />
          <GateCashCard amount={gateCash} />
        </aside>
      </div>

      <CheckInConfirmDialog
        soldier={sel}
        nextNumber={nextNumber}
        price={price}
        coverUrl={coverUrl}
        loading={loading}
        onCancel={cancel}
        onConfirm={confirm}
      />
      <CheckInSuccess
        result={result}
        operationTag={`${sequence} • Recruta`}
        yearLabel={`Aliste-se • ${new Date().getFullYear()}`}
        price={price}
        onNext={next}
      />
    </>
  );
};
